import { css } from 'styled-components';
import i18next from 'i18next';
import moment from 'moment';
import React from 'react';

import { formatDateWithLanguageCode, formatTimeWithLanguageCode } from '@commons/utils/date';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { InpulseLabel } from '@commons/DeepsightComponents';
import { labelContainerStyle } from '@commons/constants/labelStyle';
import { Tags } from '@commons/Tags';
import DisplayNumber from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { getExternalOrderStatusProperties } from '../constants';

import { getExternalOrderChannelLabel } from './getExternalOrderChannelLabel';

// The label carries the `box` class, whose own rule sets side margins: the override has to be
// scoped the same way to center the tag in its cell
const CHANNEL_LABEL_CUSTOM_STYLE = css`
  &.box {
    margin: 0;
  }
`;

const renderCellText = (value) => (
  <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
    {value || '-'}
  </Text>
);

const renderDeliveryDate = (deliveryDate, userLanguageCode) =>
  renderCellText(formatDateWithLanguageCode(moment(deliveryDate), userLanguageCode));

const renderDeliveryTime = (deliveryTime, userLanguageCode) => {
  if (!deliveryTime) {
    return renderCellText(null);
  }

  return renderCellText(formatTimeWithLanguageCode(deliveryTime, userLanguageCode));
};

const renderChannel = (channelName, externalOrder) => {
  const channelLabel = getExternalOrderChannelLabel(externalOrder);

  if (!channelLabel) {
    return renderCellText(null);
  }

  return <Tags items={[channelLabel]} labelCustomStyle={CHANNEL_LABEL_CUSTOM_STYLE} onlyLabel />;
};

const renderStatus = (status) => {
  const statusProperties = getExternalOrderStatusProperties()[status];

  if (!statusProperties) {
    return renderCellText(null);
  }

  return (
    <div style={labelContainerStyle}>
      <InpulseLabel color={statusProperties.color} text={statusProperties.name} type={'tertiary'} />
    </div>
  );
};

/**
 * Columns of the external orders list. ListView sorts on `propertyKey`, so they are named after the
 * `orderBy` keys accepted by POST /v2/b2b/external-orders/paginated. The `Canal` column is only
 * relevant on a multi-channel platform, where the row otherwise always carries the default channel.
 * The filters are declared apart in `getExternalOrderFilterList`: they key on the ids the request
 * body expects (`externalClientId`, `channelId`) rather than on the displayed names.
 *
 * @param {Object} params
 * @param {string} params.userLanguageCode - The language code used to format the delivery date
 * @param {string} params.storeName - The client wording for a store
 * @param {boolean} params.hasMultipleChannels - Whether the platform handles several channels
 *
 * @returns {Array<Object>} The ListView columns
 */
export const getExternalOrderColumns = ({ userLanguageCode, storeName, hasMultipleChannels }) => {
  const columns = [
    {
      id: 'externalClientName',
      propertyKey: 'externalClientName',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CLIENT'),
      render: renderCellText,
    },
    {
      id: 'storeName',
      propertyKey: 'storeName',
      name: getClientStoreNameTranslation(storeName),
      render: renderCellText,
    },
    {
      id: 'deliveryDate',
      propertyKey: 'deliveryDate',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_DATE'),
      render: (deliveryDate) => renderDeliveryDate(deliveryDate, userLanguageCode),
    },
    {
      id: 'deliveryTime',
      propertyKey: 'deliveryTime',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_TIME'),
      render: (deliveryTime) => renderDeliveryTime(deliveryTime, userLanguageCode),
    },
  ];

  if (hasMultipleChannels) {
    columns.push({
      id: 'channelName',
      propertyKey: 'channelName',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CHANNEL'),
      render: renderChannel,
    });
  }

  columns.push(
    {
      id: 'totalWithTaxes',
      propertyKey: 'totalWithTaxes',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_TOTAL_WITH_TAXES'),
      render: (totalWithTaxes) => (
        <DisplayNumber
          font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
          number={totalWithTaxes}
          displayCurrencyCode
        />
      ),
    },
    {
      id: 'status',
      propertyKey: 'status',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_STATUS'),
      render: renderStatus,
    },
  );

  return columns;
};
